<template>
  <picture v-if="availableSizes.length" class="compressed-image">
    <div v-if="!loaded" class="skeleton compressed-image__skeleton" />
    <source
      v-for="[size, img] in availableSizes"
      :key="size"
      :media="getMedia(size)"
      :srcset="getCompleteSrcset(img)"
    />
    <img
      :class="classes"
      :loading="loading"
      :src="defaultImageSrc"
      :srcset="getCompleteSrcset(defaultImage)"
      :alt="alt"
      :style="{ objectFit, objectPosition }"
      :draggable="draggable"
      @load="onLoad"
      @error="onError"
    />
  </picture>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useImages } from 'hooks/useImages.js'

const props = defineProps({
  compressedImages: {
    type: Object,
    default: () => {},
  },
  alt: {
    type: String,
    default: '',
  },
  objectFit: {
    type: String,
    default: 'cover',
  },
  objectPosition: {
    type: String,
    default: 'center',
  },
  loading: {
    type: String,
    default: '',
  },
  draggable: {
    type: Boolean,
    default: true,
  },
  classes: {
    type: Array,
    default: () => [],
  },
  media: {
    type: Object,
    default: () => {},
  },
})

const emit = defineEmits(['error'])

const defaultCompressedImageMedia = {
  xs: '(max-width: 400px)',
  sm: '(max-width: 600px)',
  md: '(max-width: 767px)',
  lg: '(max-width: 1023px)',
  xl: '(min-width: 1024px)',
}

const { getCompleteSrcset, cdnDomain } = useImages()
const loaded = ref(false)

const availableSizes = computed(() => {
  if (!props.compressedImages || !Object.keys(props.compressedImages).length) {
    return []
  }
  const images = props.compressedImages
  return Object.entries(images).filter((item) => {
    return item[1] !== 'SizeType' && item[1] && item[0] !== 'id' && item[0] !== 'name' && item[0] !== 'size_settings'
  })
})

const defaultImage = computed(() => availableSizes.value[availableSizes.value.length - 1][1])
const defaultImageSrc = computed(() => cdnDomain.value + defaultImage.value)

function onLoad() {
  loaded.value = true
}
function onError(e) {
  emit('error', e)
  loaded.value = true
}
function getMedia(size) {
  switch (size) {
    case 'xs':
      return props.media?.xs ? props.media.xs : defaultCompressedImageMedia.xs
    case 'sm':
      return props.media?.sm ? props.media.sm : defaultCompressedImageMedia.sm
    case 'md':
      return props.media?.md ? props.media.md : defaultCompressedImageMedia.md
    case 'lg':
      return props.media?.lg ? props.media.lg : defaultCompressedImageMedia.lg
    case 'xl':
      return props.media?.xl ? props.media.xl : defaultCompressedImageMedia.xl
  }
}
</script>

<style lang="scss">
.compressed-image {
  position: relative;
  &__skeleton {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
  }
  img {
    width: 100%;
    height: 100%;
  }
}
</style>
